<script setup>
import { computed, inject, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ChecklistQuerySection from '@/components/admin/report-qc/ChecklistQuerySection.vue'

/**
 * RED-386 + RED-361: the one checklist editor. One drawer edits an item (the
 * rule) and every question to the engine that checks it (the work order), so
 * Toomas sees both texts in one panel and knows which one he edits. Edits the
 * shared draft directly, so the preview and the save rules follow every
 * change; the editor takes a snapshot when it opens the drawer and puts it
 * back when the drawer closes without `Valmis` / `Lisa kontrollpunkt`.
 *
 * The question list is frozen when the drawer opens: unticking this item in a
 * question's picker must not make that section vanish mid-edit; it is gone the
 * next time the drawer opens. `Seo küsimusega` is the only way to give an item
 * (a new one above all) one more question. Opened from `Saatmise järjekord` for
 * a question that covers no item, the drawer shows only that question.
 *
 * The block is chosen only for a new item; an existing item's block and every
 * number stay as they are.
 *
 * RED-361 (compact, Siim 2026-10-21): the hints sit in tooltips next to their
 * labels, Plokk and Number share one row, and `Seo küsimusega` hides behind a
 * text button while the item already has a question. With no question the
 * picker stays open with the D1 error, because then a question is required.
 */
const props = defineProps({
  number: { type: Number, default: null },
  isNew: { type: Boolean, default: false },
  // The question to scroll to (a row of `Saatmise järjekord`, or a question block on an item row).
  focusKey: { type: String, default: null },
  // RED-361: opened from a Tagasiside row: the checklist version that feedback was given on.
  feedbackVersion: { type: Number, default: null },
  currentVersion: { type: Number, default: null },
})

const emit = defineEmits(['apply', 'cancel'])

const visible = defineModel('visible', { type: Boolean, default: false })

const { t } = useI18n()
const { draft, problemsFor, queryTitle, tip } = inject('checklistDraft')

const sectionKeys = ref([])
// A new token per opening remounts the sections, so no edit mode or open picker carries over.
const opening = ref(0)
const linkChoice = ref(null)
const linkOpen = ref(false)

const item = computed(() => (props.number === null ? null : (draft.items.find(row => row.number === props.number) ?? null)))

const inSendOrder = queries => [...queries].sort((a, b) => a.sort_order - b.sort_order)

watch(visible, open => {
  if (!open) {
    return
  }

  opening.value++
  linkChoice.value = null
  linkOpen.value = false
  sectionKeys.value =
    props.number === null
      ? [props.focusKey].filter(Boolean)
      : inSendOrder(draft.queries.filter(query => query.covers_items.includes(props.number))).map(query => query.key)
})

const linkOptions = computed(() =>
  inSendOrder(draft.queries.filter(query => !sectionKeys.value.includes(query.key))).map(query => ({
    value: query.key,
    label: `${query.key} · ${queryTitle(query)}`,
  })),
)

const link = key => {
  const query = draft.queries.find(row => row.key === key)

  if (!query || props.number === null) {
    return
  }

  query.covers_items = [...new Set([...query.covers_items, props.number])].sort((a, b) => a - b)
  sectionKeys.value = [...sectionKeys.value, key]
  nextTick(() => (linkChoice.value = null))
}

const blockOptions = computed(() => [
  { value: 'standard', label: t('messages.admin.report_qc.checklist.block_standard') },
  { value: 'praktika', label: t('messages.admin.report_qc.checklist.block_praktika') },
])

const olderFeedback = computed(() => props.feedbackVersion !== null && props.currentVersion !== null && props.feedbackVersion < props.currentVersion)

const errorOf = field => problemsFor('item', props.number).find(problem => problem.field.endsWith(`.${field}`))?.message

const header = computed(() => {
  if (props.number === null) {
    return t('messages.admin.report_qc.checklist.edit_query')
  }

  return t(props.isNew ? 'messages.admin.report_qc.checklist.item_new_title' : 'messages.admin.report_qc.checklist.item_edit_title')
})

const scrollToFocus = () => {
  if (props.focusKey && props.number !== null) {
    document.querySelector(`[data-testid="checklist-query-section-${props.focusKey}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }
}
</script>

<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :header="header"
    class="w-full! md:w-[40rem]!"
    data-testid="checklist-item-drawer"
    @show="scrollToFocus"
    @hide="emit('cancel')"
  >
    <Fluid>
      <div class="flex flex-col gap-5">
        <Message v-if="olderFeedback" severity="info" :closable="false" data-testid="checklist-item-feedback-version">
          {{ t('messages.admin.report_qc.checklist.feedback_version', { n: feedbackVersion, m: currentVersion }) }}
        </Message>
        <section v-if="item" class="flex flex-col gap-5" data-testid="checklist-item-section">
          <h3 class="flex items-center gap-2 text-base font-medium">
            {{ t('messages.admin.report_qc.checklist.section_item') }}
            <i
              class="pi pi-info-circle text-sm text-gray-400"
              tabindex="0"
              v-tooltip.top="tip(t('messages.admin.report_qc.checklist.section_item_tip'))"
              :aria-label="t('messages.admin.report_qc.checklist.section_item_tip')"
              data-testid="checklist-item-section-info"
            />
          </h3>

          <div class="flex items-center gap-3">
            <ToggleSwitch v-model="item.active" input-id="rqc-item-active" data-testid="checklist-item-active" />
            <label for="rqc-item-active" class="text-sm font-medium">{{ t('messages.admin.report_qc.checklist.active') }}</label>
          </div>

          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div class="flex flex-wrap items-center gap-2 text-sm">
              <span class="font-medium">{{ t('messages.admin.report_qc.checklist.block') }}</span>
              <SelectButton
                v-if="isNew"
                v-model="item.block"
                :options="blockOptions"
                option-label="label"
                option-value="value"
                :allow-empty="false"
                class="w-auto!"
                data-testid="checklist-item-block"
              />
              <span v-else class="flex items-center gap-2 text-gray-700" data-testid="checklist-item-block-locked">
                <i class="pi pi-lock text-gray-400" aria-hidden="true" />
                {{ blockOptions.find(option => option.value === item.block)?.label }}
                <i
                  class="pi pi-exclamation-triangle text-amber-500"
                  tabindex="0"
                  v-tooltip.top="tip(t('messages.admin.report_qc.checklist.block_hint'))"
                  :aria-label="t('messages.admin.report_qc.checklist.block_hint')"
                  data-testid="checklist-item-block-warning"
                />
              </span>
            </div>

            <div class="flex items-center gap-2 text-sm">
              <span class="font-medium">{{ t('messages.admin.report_qc.checklist.number') }}</span>
              <span class="font-mono" data-testid="checklist-item-number">{{ item.number }}</span>
              <i
                class="pi pi-info-circle text-gray-400"
                tabindex="0"
                v-tooltip.top="tip(t('messages.admin.report_qc.checklist.number_hint'))"
                :aria-label="t('messages.admin.report_qc.checklist.number_hint')"
                data-testid="checklist-item-number-info"
              />
            </div>
          </div>

          <div>
            <label for="rqc-item-title" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.checklist.title_label') }}</label>
            <InputText id="rqc-item-title" v-model="item.title" :maxlength="255" :invalid="!!errorOf('title')" data-testid="checklist-item-title" />
            <p class="mt-1 text-sm text-red-600" :class="{ invisible: !errorOf('title') }">{{ errorOf('title') || '&nbsp;' }}</p>
          </div>

          <div>
            <div class="mb-1 flex items-center gap-2">
              <label for="rqc-item-body" class="text-sm font-medium">{{ t('messages.admin.report_qc.checklist.text') }}</label>
              <i
                class="pi pi-info-circle text-gray-400"
                tabindex="0"
                v-tooltip.top="tip(t('messages.admin.report_qc.checklist.item_text_hint'))"
                :aria-label="t('messages.admin.report_qc.checklist.item_text_hint')"
                data-testid="checklist-item-text-info"
              />
            </div>
            <Textarea id="rqc-item-body" v-model="item.body" rows="5" auto-resize :invalid="!!errorOf('body')" data-testid="checklist-item-body" />
            <p class="mt-1 text-sm text-red-600" :class="{ invisible: !errorOf('body') }">{{ errorOf('body') || '&nbsp;' }}</p>
          </div>
        </section>

        <ChecklistQuerySection v-for="key in sectionKeys" :key="`${opening}-${key}`" :query-key="key" :item-number="number" />

        <section v-if="item" class="flex flex-col gap-2 border-t border-gray-200 pt-5" data-testid="checklist-item-link">
          <template v-if="sectionKeys.length === 0">
            <h3 class="text-base font-medium">{{ t('messages.admin.report_qc.checklist.section_query') }}</h3>
            <p class="text-sm text-gray-600" data-testid="checklist-item-no-query">{{ t('messages.admin.report_qc.checklist.row_off') }}</p>
          </template>
          <div v-else-if="!linkOpen" class="flex items-center gap-2">
            <Button
              :label="t('messages.admin.report_qc.checklist.link_add')"
              icon="pi pi-plus"
              size="small"
              text
              class="w-auto!"
              data-testid="checklist-item-link-add"
              @click="linkOpen = true"
            />
            <i
              class="pi pi-info-circle text-gray-400"
              tabindex="0"
              v-tooltip.top="tip(t('messages.admin.report_qc.checklist.link_hint'))"
              :aria-label="t('messages.admin.report_qc.checklist.link_hint')"
              data-testid="checklist-item-link-info"
            />
          </div>
          <template v-if="sectionKeys.length === 0 || linkOpen">
            <label for="rqc-item-link" class="text-sm font-medium">{{ t('messages.admin.report_qc.checklist.link_label') }}</label>
            <Select
              v-model="linkChoice"
              input-id="rqc-item-link"
              :options="linkOptions"
              option-label="label"
              option-value="value"
              :placeholder="t('messages.admin.report_qc.checklist.link_placeholder')"
              filter
              data-testid="checklist-item-link-select"
              @update:model-value="key => key && link(key)"
            />
            <p class="text-xs text-gray-500">{{ t('messages.admin.report_qc.checklist.link_hint') }}</p>
          </template>
          <p class="text-sm text-red-600" :class="{ invisible: !errorOf('active') }" data-testid="checklist-item-d1">{{ errorOf('active') || '&nbsp;' }}</p>
        </section>

        <div class="flex flex-wrap items-center justify-end gap-3">
          <p class="text-xs text-gray-500" data-testid="checklist-item-apply-hint">{{ t('messages.admin.report_qc.checklist.apply_hint') }}</p>
          <Button
            :label="t(isNew ? 'messages.admin.report_qc.checklist.add_item' : 'messages.admin.report_qc.checklist.apply')"
            icon="pi pi-check"
            class="w-auto!"
            data-testid="checklist-item-apply"
            @click="emit('apply')"
          />
        </div>
      </div>
    </Fluid>
  </Drawer>
</template>
